import { useState, useEffect, useRef, useCallback } from "react";
import GridSearchBar from "../../../../../common/components/grid-search-bar/GridSearchBar";
import LeadsProductCard from "../../../../../common/components/leads-product-card/LeadsProductCard";
import CommonSideDialog from "../../../../../common/components/side-dialog/CommonSideDialog";
import styles from "./RecommendProductDialogue.module.scss";
import { useLazyGetProductsWithLoanQuery } from "../../../../../service/solar-product-service";
import { Category, StoreProductDetail, PaymentPlan, PhysicalLoadAssessment, DigitalLoadAssessment } from "../../../../../model/leads/leads";
import LoadAssessmentSummary from "./LoadAssessmentSummary";

interface RecommendProductDialogueProps {
	onClose: () => void;
	onSaveProducts?: (products: StoreProductDetail[]) => void;
	paymentPlan?: PaymentPlan;
	loadAssessmentInfo: PhysicalLoadAssessment | DigitalLoadAssessment;
	previouslyRecommendedProduct?: StoreProductDetail[];
}
const PAGE_SIZE = 12;

const RecommendProductDialogue = ({ onClose, onSaveProducts, paymentPlan, loadAssessmentInfo, previouslyRecommendedProduct = [] }: RecommendProductDialogueProps) => {
	const [fetchProducts, { data, isLoading, isFetching }] = useLazyGetProductsWithLoanQuery();
	const [products, setProducts] = useState<StoreProductDetail[]>([]);
	const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
	const [previouslyRecommendedProductIds, setPreviouslyRecommendedProductIds] = useState<string[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [page, setPage] = useState<number>(1);
	const [hasMore, setHasMore] = useState<boolean>(true);
	const [filteredProducts, setFilteredProducts] = useState<StoreProductDetail[]>([]);
	
	const observer = useRef<IntersectionObserver | null>(null);
	const lastProductElementRef = useCallback((node: HTMLButtonElement) => {
		if (isLoading || isFetching) return;
		if (observer.current) observer.current.disconnect();
		
		observer.current = new IntersectionObserver(entries => {
			if (entries[0].isIntersecting && hasMore) {
				setPage(prevPage => prevPage + 1);
			}
		});
		
		if (node) observer.current.observe(node);
	}, [isLoading, isFetching, hasMore]);

	const isPayAsYouGo = paymentPlan === PaymentPlan.PayAsYouGo;

	useEffect(() => {
		const previousIds = previouslyRecommendedProduct
			.map(product => product.id)
			.filter((id): id is string => id !== undefined);
		
		setPreviouslyRecommendedProductIds(previousIds);
		setSelectedProducts(previousIds); 
	}, [previouslyRecommendedProduct]);

	const hasNewSelections = () => {
		if (previouslyRecommendedProductIds.length === 0) {
			return selectedProducts.length > 0;
		}
		
		const sortedSelected = [...selectedProducts].sort();
		const sortedPrevious = [...previouslyRecommendedProductIds].sort();
		
		if (sortedSelected.length !== sortedPrevious.length) {
			return true;
		}
		
		return sortedSelected.some((id, index) => id !== sortedPrevious[index]);
	};

	useEffect(() => {
		fetchProducts({
			page,
			pageSize: PAGE_SIZE,
		});
	}, [fetchProducts, page]);

	useEffect(() => {
		if (data) {
			setProducts(prevProducts => {
				const newProducts = [...prevProducts];
				data.items.forEach(item => {
					if (!newProducts.some((p : StoreProductDetail) => p.id === item.id)) {
						newProducts.push(item);
					}
				});
				return newProducts;
			});
			
			setHasMore(data.count > page * PAGE_SIZE);
		}
	}, [data, page]);

	useEffect(() => {
		const filtered = searchTerm.trim() === '' 
			? products 
			: products.filter(product => 
				product.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
				product.packageDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				product.categories?.some(category => 
					category.name.toLowerCase().includes(searchTerm.toLowerCase())
				)
			);
		
		setFilteredProducts(filtered);
	}, [searchTerm, products]);

	const handleProductClick = (product: StoreProductDetail) => {
		if (!product.id) return;
		
		if (previouslyRecommendedProductIds.includes(product.id)) {
			return;
		}
		
		setSelectedProducts(prev => {
			if (prev.includes(product.id!)) {
				return prev.filter(id => id !== product.id);
			}
			
			if (isPayAsYouGo) {
				const keptPreviouslyRecommended = prev.filter(id => 
					previouslyRecommendedProductIds.includes(id)
				);
				return [...keptPreviouslyRecommended, product.id!];
			}
			
			return [...prev, product.id!];
		});
	};
	
	const handleSearch = (term: string) => {
		setSearchTerm(term);
	};
	
	const handleSave = () => {
		if (onSaveProducts) {
			const selectedProductObjects = products.filter(product => 
				product.id && selectedProducts.includes(product.id)
			);
			onSaveProducts(selectedProductObjects);
		}
		onClose();
	};

	return (
		<CommonSideDialog onCloseDialog={onClose}>
			<div className={styles.modal}>
				<div className={styles.modal_content}>
					<div className={styles.head}>
						<h2 className={styles.title}>Recommend Products</h2>
						<button onClick={onClose} className={styles.modal_close}>
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									d="M12 12.9436L16.3282 17.2718C16.453 17.3966 16.606 17.4632 16.7872 17.4718C16.9684 17.4804 17.1299 17.4137 17.2718 17.2718C17.4137 17.1299 17.4846 16.9726 17.4846 16.8C17.4846 16.6274 17.4137 16.4701 17.2718 16.3282L12.9436 12L17.2718 7.6718C17.3966 7.54702 17.4632 7.39403 17.4718 7.21283C17.4804 7.03163 17.4137 6.87009 17.2718 6.7282C17.1299 6.58631 16.9726 6.51537 16.8 6.51537C16.6274 6.51537 16.4701 6.58631 16.3282 6.7282L12 11.0564L7.6718 6.7282C7.54702 6.60342 7.39403 6.53676 7.21283 6.5282C7.03163 6.51964 6.87009 6.58631 6.7282 6.7282C6.58631 6.87009 6.51537 7.02736 6.51537 7.2C6.51537 7.37264 6.58631 7.52991 6.7282 7.6718L11.0564 12L6.7282 16.3282C6.60342 16.453 6.53676 16.606 6.5282 16.7872C6.51964 16.9684 6.58631 17.1299 6.7282 17.2718C6.87009 17.4137 7.02736 17.4846 7.2 17.4846C7.37264 17.4846 7.52991 17.4137 7.6718 17.2718L12 12.9436Z"
									fill="#7F7D82"
								/>
							</svg>
						</button>
					</div>

					<div className={styles.modal_body}>
						<p className={styles.ins_text}>
							Please select {isPayAsYouGo ? 'a product' : 'products'} that fits the customer's energy needs
							{isPayAsYouGo && (
								<span className={styles.payg_notice}> (Pay-as-you-go customers can only select one product)</span>
							)}
							{previouslyRecommendedProductIds.length > 0 && (
								<span className={styles.previous_notice}> (Previously recommended products are pre-selected and cannot be removed)</span>
							)}
						</p>
						<GridSearchBar 
							width="100%" 
							placeholder="Search by product name, description, or category" 
							onChangeHandler={handleSearch} 
						/>
						<LoadAssessmentSummary assessment={loadAssessmentInfo} /> 
						{isLoading && page === 1 ? (
							<div className={styles.loading}>Loading products...</div>
						) : filteredProducts.length === 0 ? (
							<div className={styles.no_results}>No products match your search criteria</div>
						) : (
							<div className={styles.product_row}>
								{filteredProducts.map((product, index) => {
									const categories = product.categories?.map(cat => ({
										id: cat.id || '',
										name: cat.name
									} as Category)) || [];
									
									const isSelected = selectedProducts.includes(product.id || '');
									const isPreviouslyRecommended = previouslyRecommendedProductIds.includes(product.id || '');
									const isDisabled = (isPayAsYouGo && selectedProducts.length > 0 && !isSelected && !isPreviouslyRecommended) || isPreviouslyRecommended;
									
									return (
										<button
											ref={index === filteredProducts.length - 1 ? lastProductElementRef : null}
											id={product.id}
											className={`${styles.selectedProduct} ${isSelected ? styles.selectedItem : ""} ${isDisabled ? styles.disabledProduct : ""} ${isPreviouslyRecommended ? styles.previouslyRecommended : ""}`}
											key={product.id}
											onClick={() => handleProductClick(product)}
											disabled={isDisabled}
										>
											<LeadsProductCard
												productName={product.productName}
												price={product?.priceBreakdown?.price}
												capacity={product.productCapacity}
												description={product.packageDescription || ''}
												imageSrc={(product.images && product.images.length > 0) ? product.images[0].imageUrl : '/placeholder-image.png'}
												categoryNames={categories}
											/>
											{isPreviouslyRecommended && (
												<div className={styles.previously_recommended_overlay}>
													<span>Previously Recommended</span>
												</div>
											)}
											{isDisabled && !isPreviouslyRecommended && (
												<div className={styles.disabled_overlay}>
													<span>Only one product allowed for Pay-as-you-go</span>
												</div>
											)}
										</button>
									);
								})}
								
								{isFetching && hasMore && (
									<div className={styles.loading_more}>Loading more products...</div>
								)}
							</div>
						)}
					</div>
					
					<div className={styles.modal_actions}>
						<button 
							className={`${styles.btn} ${styles.btn_green_outline}`}
							onClick={onClose}
						>
							Cancel
						</button>
						<button 
							className={`${styles.btn} ${styles.btn_green}`}
							onClick={handleSave}
							disabled={!hasNewSelections()}
						>
							Save {selectedProducts.length > 0 ? `(${selectedProducts.length})` : ''}
						</button>
					</div>
				</div>
			</div>
		</CommonSideDialog>
	);
};

export default RecommendProductDialogue;